/**
 * Step definitions for the Moshky Shopify welcome tour.
 *
 * DATA-INTEGRITY RULE (CLAUDE.md): every sentence here is either generically true of the
 * product, or derived from `TourFacts` — which is built entirely from live API responses in
 * useShopifyTourFacts.ts. Nothing in this file may invent a count, a percentage, a benchmark,
 * or a "customers like you…" claim. When a fact isn't available (still loading, or the API
 * failed), the step says nothing about it and the deep link carries the step on its own.
 *
 * Each stop points at a REAL route that exists in Routes.tsx today — verified, not assumed.
 */

export interface TourFacts {
  /** Shopify store domain from GET Shopify/Integrations/ (field is `domain`). */
  storeDomain: string | null;
  storeName: string | null;
  /** ScriptTag-based widget install flag from the same payload. Null = unknown. */
  widgetDeployed: boolean | null;
  /** Shopify skills the site's real subscription currently includes (server-resolved `included`). */
  activeShopifySkillCount: number | null;
  /** Shopify skills the backend knows about at all, for the "N of M" denominator. */
  totalShopifySkillCount: number | null;
  chatWindowCount: number | null;
  /** The AI configuration auto-provisioning created, if it's still identifiable. */
  assistantName: string | null;
  assistantId: number | null;
  workflowName: string | null;
  workflowId: number | null;
  workflowEnabled: boolean | null;
  /** Whether the site's default Chat Experience bundle has a pre-chat survey attached. */
  hasPreChatSurvey: boolean | null;
}

export interface TourStep {
  key: string;
  /** Short label for the progress rail. */
  label: string;
  title: string;
  /** Always-true framing sentence. Never a statistic. */
  body: string;
  /** Live one-liner, or null when the underlying fact isn't available. */
  fact: (facts: TourFacts) => string | null;
  /** Internal route — rendered with <Link to>, never a hardcoded absolute URL. */
  to: (facts: TourFacts) => string;
  linkLabel: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    key: "shopify-connection",
    label: "Your store",
    title: "See what Shopify brought in",
    body: "Your store's connection is already live. This page shows the store Velaro is reading from, and whether the chat widget is on your storefront yet.",
    fact: (f) => {
      const store = f.storeName || f.storeDomain;
      if (!store) return null;
      if (f.widgetDeployed === null) return `Connected store: ${store}.`;
      return f.widgetDeployed
        ? `Connected store: ${store}. The chat widget is installed.`
        : `Connected store: ${store}. The chat widget isn't installed yet — this page has the install button.`;
    },
    to: () => "/integrations/Shopify",
    linkLabel: "Open Shopify"
  },
  {
    key: "ai-skills",
    label: "AI skills",
    title: "Check your AI skills",
    body: "Skills are the things your bot can actually do against your store — look up an order, check stock, find a customer. Each one can be turned off, or set to ask before it runs.",
    fact: (f) => {
      if (f.activeShopifySkillCount === null) return null;
      if (f.totalShopifySkillCount === null)
        return `${f.activeShopifySkillCount} Shopify skills are active on your plan.`;
      return `${f.activeShopifySkillCount} of ${f.totalShopifySkillCount} Shopify skills are active on your plan.`;
    },
    to: () => "/integrations/Shopify?tab=skills",
    linkLabel: "Open AI Skills"
  },
  {
    key: "chat-window",
    label: "Chat window",
    title: "Brand the chat window",
    body: "The chat window designer controls colors, logo, wording and placement. Changes preview live before you publish them.",
    fact: (f) =>
      f.chatWindowCount === null
        ? null
        : f.chatWindowCount === 0
          ? "You don't have a chat window design yet — you can create one here."
          : `You have ${f.chatWindowCount} chat window ${f.chatWindowCount === 1 ? "design" : "designs"}.`,
    to: () => "/chat_window",
    linkLabel: "Open the designer"
  },
  {
    key: "store-assistant",
    label: "Assistant",
    title: "Meet your Store Assistant",
    body: "This is the bot answering your shoppers: its persona, tone, knowledge and escalation rules all live here.",
    fact: (f) => (f.assistantName ? `Your assistant is "${f.assistantName}".` : null),
    to: (f) =>
      f.assistantId ? `/ai-new/settings/persona/${f.assistantId}` : "/ai-new/settings",
    linkLabel: "Open the assistant"
  },
  {
    key: "opening-workflow",
    label: "First reply",
    title: "See what happens when a chat opens",
    body: "A workflow decides the first move on every new conversation — the greeting that goes out, and which AI handles the reply from there.",
    fact: (f) => {
      if (!f.workflowName) return null;
      return f.workflowEnabled === false
        ? `"${f.workflowName}" runs on conversation opened. It's currently turned off.`
        : `"${f.workflowName}" runs on conversation opened.`;
    },
    to: (f) => (f.workflowId ? `/workflows/${f.workflowId}` : "/workflows"),
    linkLabel: "Open the workflow"
  },
  {
    key: "pre-chat",
    label: "Lead capture",
    title: "Ask for an email first",
    body: "A pre-chat survey collects a name and email before the conversation starts, so an unfinished chat still leaves you someone to follow up with. You attach one to your chat experience here.",
    fact: (f) =>
      f.hasPreChatSurvey === null
        ? null
        : f.hasPreChatSurvey
          ? "Your default chat experience already asks for details before the chat starts."
          : "Your default chat experience doesn't ask for details yet.",
    to: () => "/chat-experience",
    linkLabel: "Open Chat Experience"
  },
  {
    key: "integrations",
    label: "Connect more",
    title: "Connect the pieces",
    body: "Shopify is one of many. Connecting your helpdesk, CRM or phone system lets the same assistant answer with that context too.",
    fact: () => null,
    to: () => "/integrations",
    linkLabel: "Browse integrations"
  }
];
